import { useEffect, useState } from 'react';
import firebase from 'firebase/app';

/**
 * The customer's own details, shown in the header, footer and Help pop-up.
 * Stored in settings/organisation and settings/help (anyone with a role can read; admins edit in
 * Admin > Settings). Each customer has their own database, so these are theirs alone.
 */
export type OrgSettings = {
  companyName: string;
  databaseName: string;
  logoURL: string;
  helpName: string;
  helpEmail: string;
  helpPhone: string;
  helpHours: string;
  helpMessage: string;
};

export const ORG_DEFAULTS: OrgSettings = {
  companyName: 'CoBox',
  databaseName: 'Production Archive',
  logoURL: '',
  helpName: '',
  helpEmail: '',
  helpPhone: '',
  helpHours: '',
  helpMessage: "Can't find a file? Tell us the project name and we'll help.",
};

let cache: OrgSettings | null = null;
const listeners = new Set<(s: OrgSettings) => void>();
let started = false;

function start() {
  if (started) return;
  started = true;
  const db = firebase.firestore();
  let org: Partial<OrgSettings> = {};
  let help: Partial<OrgSettings> = {};
  const publish = () => {
    cache = { ...ORG_DEFAULTS, ...strip(org), ...strip(help) };
    listeners.forEach((fn) => fn(cache!));
  };
  // Listen only once signed in; the rules give no access before that.
  firebase.auth().onAuthStateChanged((user) => {
    if (!user) return;
    db.collection('settings')
      .doc('organisation')
      .onSnapshot((d) => {
        org = (d.data() as Partial<OrgSettings>) || {};
        publish();
      }, () => undefined);
    db.collection('settings')
      .doc('help')
      .onSnapshot((d) => {
        help = (d.data() as Partial<OrgSettings>) || {};
        publish();
      }, () => undefined);
  });
}

const strip = (o: Partial<OrgSettings>) => {
  const out: Partial<OrgSettings> = {};
  (Object.keys(ORG_DEFAULTS) as (keyof OrgSettings)[]).forEach((k) => {
    const v = (o as any)[k];
    if (typeof v === 'string' && v.trim()) (out as any)[k] = v.trim();
  });
  return out;
};

const useOrgSettings = (): OrgSettings => {
  const [s, setS] = useState<OrgSettings>(cache || ORG_DEFAULTS);
  useEffect(() => {
    start();
    listeners.add(setS);
    if (cache) setS(cache);
    return () => {
      listeners.delete(setS);
    };
  }, []);
  return s;
};

export default useOrgSettings;
